// components/workspace-timeline/presentation.ts
import { AUDIO_GAIN_MAX, clampTimelineNumber } from '#lib/workspace-timeline';
import type { DiffusionParams } from '#types/types';
import type {
  ResolvedWorkspaceTimelineTrack,
  TimelineRangePresentation,
  TimelineToolState,
  TimelineTrackBlockState,
  WorkspaceTimelineTrack,
} from './types';

export { AUDIO_GAIN_MAX };
export const AUDIO_GAIN_SLIDER_MIN = 0;
export const AUDIO_GAIN_SLIDER_MAX = 100;
const AUDIO_GAIN_SLIDER_MID = 50;
export const TIMELINE_ID_SIGNATURE_SEPARATOR = '\u001F';

export const clamp = clampTimelineNumber;

export const cloneTimelineValue = <T,>(value: T): T => {
  if (value === null || typeof value === 'undefined') return value;
  if (typeof value !== 'object') return value;
  try {
    if (typeof structuredClone === 'function') {
      return structuredClone(value);
    }
  } catch {
    // fallback below
  }
  return JSON.parse(JSON.stringify(value)) as T;
};

export const mergeTimelineAudioParams = (
  params: unknown,
  patch: Record<string, boolean | number | string | null>
): DiffusionParams => {
  const base =
    params && typeof params === 'object' && !Array.isArray(params)
      ? (params as Record<string, unknown>)
      : {};
  return {
    ...base,
    ...patch,
  } as unknown as DiffusionParams;
};

const hashString = (value: string): number => {
  let hash = 0;
  for (let index = 0; index < value.length; index += 1) {
    hash = ((hash << 5) - hash + value.charCodeAt(index)) | 0;
  }
  return hash >>> 0;
};

export const buildTrackWaveformBins = (seed: string, count = 72): number[] => {
  const bins: number[] = [];
  let generator = hashString(seed) || 1;
  for (let index = 0; index < count; index += 1) {
    generator = (generator * 1664525 + 1013904223) >>> 0;
    const noise = ((generator >> 16) & 0xff) / 255;
    const envelope = 0.45 + 0.55 * Math.abs(Math.sin(((index + 1) / count) * Math.PI * 2.2));
    bins.push(0.18 + 0.82 * envelope * (0.35 + 0.65 * noise));
  }
  return bins;
};

export const buildIdSignature = (ids: readonly unknown[] | null | undefined): string => {
  if (!Array.isArray(ids)) return '';
  return Array.from(new Set(ids.filter((id): id is string => typeof id === 'string' && id.length > 0)))
    .sort((left, right) => left.localeCompare(right))
    .join(TIMELINE_ID_SIGNATURE_SEPARATOR);
};

export const signatureHasId = (signature: string, id: string): boolean => {
  if (!signature || !id) return false;
  return `${TIMELINE_ID_SIGNATURE_SEPARATOR}${signature}${TIMELINE_ID_SIGNATURE_SEPARATOR}`.includes(
    `${TIMELINE_ID_SIGNATURE_SEPARATOR}${id}${TIMELINE_ID_SIGNATURE_SEPARATOR}`
  );
};

export const buildScopedMembershipSignature = (
  membershipSignature: string,
  ids: readonly string[]
): string => {
  if (!membershipSignature || ids.length === 0) return '';
  return ids.filter((id) => signatureHasId(membershipSignature, id)).join(TIMELINE_ID_SIGNATURE_SEPARATOR);
};

export const formatTimecode = (ms: number, fps: number = 30): string => {
  const totalMs = Math.max(0, Math.round(ms));
  const totalSeconds = Math.floor(totalMs / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  const frames = Math.floor(((totalMs % 1000) / 1000) * fps);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}:${pad(frames)}`;
};

export const getNormalizedAudioGain = (value: unknown): number => {
  if (typeof value !== 'number' || !Number.isFinite(value)) return 1;
  return clamp(value, 0, AUDIO_GAIN_MAX);
};

export const getWorkspaceTimelineTrackSource = (
  track: Pick<WorkspaceTimelineTrack, 'layerType' | 'audioSrc' | 'videoSrc'>
): string => {
  if (track.layerType === 'audio') {
    return typeof track.audioSrc === 'string' ? track.audioSrc.trim() : '';
  }
  return typeof track.videoSrc === 'string' ? track.videoSrc.trim() : '';
};

export const sliderPercentToAudioGain = (sliderPercentRaw: number): number => {
  const sliderPercent = clamp(sliderPercentRaw, AUDIO_GAIN_SLIDER_MIN, AUDIO_GAIN_SLIDER_MAX);
  if (sliderPercent <= AUDIO_GAIN_SLIDER_MIN) return 0;
  // Symmetric pace around the midpoint:
  // 50 => original (1x), 100 => AUDIO_GAIN_MAX, 0 => hard mute.
  const signedPosition =
    (sliderPercent - AUDIO_GAIN_SLIDER_MID) /
    Math.max(1, AUDIO_GAIN_SLIDER_MID - AUDIO_GAIN_SLIDER_MIN);
  const gain = Math.exp(Math.log(AUDIO_GAIN_MAX) * signedPosition);
  return clamp(gain, 0, AUDIO_GAIN_MAX);
};

export const audioGainToSliderPercent = (audioGainRaw: number): number => {
  const gain = getNormalizedAudioGain(audioGainRaw);
  if (gain <= 0.000001) return 0;
  const safeGain = clamp(gain, 1 / AUDIO_GAIN_MAX, AUDIO_GAIN_MAX);
  const signedPosition = Math.log(safeGain) / Math.log(AUDIO_GAIN_MAX);
  const sliderPercent =
    AUDIO_GAIN_SLIDER_MID +
    signedPosition * Math.max(1, AUDIO_GAIN_SLIDER_MID - AUDIO_GAIN_SLIDER_MIN);
  return Math.round(clamp(sliderPercent, AUDIO_GAIN_SLIDER_MIN, AUDIO_GAIN_SLIDER_MAX));
};

// One ghost per kind, so a file dragged in already reads as the block it will become.
export const timelineDropPreviewFill = (layerType: string | null | undefined): string => (
  layerType === 'image'
    ? 'rgba(92,92,92,0.85)'
    : layerType === 'audio'
      ? 'rgba(78,78,78,0.85)'
      : 'rgba(58,58,58,0.85)'
);

export const toAudioGainTrackColor = (audioGain: number, isTrackActive: boolean): string => {
  const sliderPercent = audioGainToSliderPercent(audioGain);
  const normalizedGain = clamp(
    sliderPercent / Math.max(1, AUDIO_GAIN_SLIDER_MAX),
    0,
    1
  );
  // Greys only: louder reads lighter, and silence stays visible above the #262626 shell.
  const low = { r: 46, g: 46, b: 46 };
  const high = { r: 112, g: 112, b: 112 };
  const mix = (a: number, b: number, t: number) => Math.round(a + (b - a) * t);
  const r = mix(low.r, high.r, normalizedGain);
  const g = mix(low.g, high.g, normalizedGain);
  const b = mix(low.b, high.b, normalizedGain);
  const brighten = isTrackActive ? 1.08 : 1;
  const toHex = (channel: number) =>
    Math.max(0, Math.min(255, Math.round(channel * brighten))).toString(16).padStart(2, '0');
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
};

const areTimelineTrackRectsEqual = (
  left: WorkspaceTimelineTrack['rect'],
  right: WorkspaceTimelineTrack['rect']
): boolean => (
  left.x === right.x &&
  left.y === right.y &&
  left.w === right.w &&
  left.h === right.h
);

export const areResolvedTimelineTracksEqual = (
  left: ResolvedWorkspaceTimelineTrack,
  right: ResolvedWorkspaceTimelineTrack
): boolean => (
  left.layerId === right.layerId &&
  left.name === right.name &&
  left.layerType === right.layerType &&
  left.isLayerVisible === right.isLayerVisible &&
  left.videoSrc === right.videoSrc &&
  left.imageSrc === right.imageSrc &&
  left.audioSrc === right.audioSrc &&
  left.separatedFromLayerId === right.separatedFromLayerId &&
  left.audioGain === right.audioGain &&
  left.mutedInTimeline === right.mutedInTimeline &&
  left.renderOrder === right.renderOrder &&
  left.startMs === right.startMs &&
  left.endMs === right.endMs &&
  left.sourceInMs === right.sourceInMs &&
  left.sourceOutMs === right.sourceOutMs &&
  left.activeDurationMs === right.activeDurationMs &&
  left.mediaDurationMs === right.mediaDurationMs &&
  left.trackLimitEndMs === right.trackLimitEndMs &&
  left.minSegmentMs === right.minSegmentMs &&
  left.laneId === right.laneId &&
  left.laneOrder === right.laneOrder &&
  left.fadeInRatio === right.fadeInRatio &&
  left.fadeOutRatio === right.fadeOutRatio &&
  left.fadeInEndMs === right.fadeInEndMs &&
  left.fadeOutStartMs === right.fadeOutStartMs &&
  left.scale === right.scale &&
  areTimelineTrackRectsEqual(left.rect, right.rect)
);

export const areTimelineTrackArraysEqual = (
  left: ResolvedWorkspaceTimelineTrack[],
  right: ResolvedWorkspaceTimelineTrack[]
): boolean => {
  if (left.length !== right.length) return false;
  for (let index = 0; index < left.length; index += 1) {
    const leftTrack = left[index];
    const rightTrack = right[index];
    if (!leftTrack || !rightTrack || !areResolvedTimelineTracksEqual(leftTrack, rightTrack)) return false;
  }
  return true;
};

export const areTimelineRangePresentationsEqual = (
  previous: TimelineRangePresentation,
  next: TimelineRangePresentation
): boolean => (
  previous.startMs === next.startMs &&
  previous.endMs === next.endMs &&
  previous.rangeMs === next.rangeMs &&
  (previous.videoInputTimelineRangeSignature ?? '') === (next.videoInputTimelineRangeSignature ?? '')
);

export const areTimelineToolStatesEqual = (
  previous: TimelineToolState,
  next: TimelineToolState
): boolean => (
  previous.isSplitToolActive === next.isSplitToolActive &&
  previous.isSoundSeparationToolActive === next.isSoundSeparationToolActive &&
  previous.isSoundVolumeToolActive === next.isSoundVolumeToolActive &&
  previous.isSoundToolActive === next.isSoundToolActive &&
  previous.canReorderTrackOrder === next.canReorderTrackOrder
);

export const areTimelineTrackBlockStatesEqual = (
  previous: TimelineTrackBlockState,
  next: TimelineTrackBlockState
): boolean => (
  previous.splitPreviewMs === next.splitPreviewMs &&
  previous.isActive === next.isActive &&
  previous.isDragging === next.isDragging &&
  previous.isSelected === next.isSelected &&
  previous.isSeparatingSoundTrack === next.isSeparatingSoundTrack &&
  previous.isMergingTrack === next.isMergingTrack &&
  previous.splitToolCursor === next.splitToolCursor
);
